const BANKID_SCHEME = "bankid:///";
const BANKID_UNIVERSAL_LINK = "https://app.bankid.com/";

/**
 * BankID's documented value for "do not redirect anywhere", and the default here
 * on every platform. Their guide reserves it for Android and desktop and asks for
 * a real return URL on iOS; we deviate on purpose.
 *
 * A return URL makes the BankID app navigate the very tab that is waiting for the
 * authentication, and on iOS that navigation can reload the document — taking the
 * React state, the orderRef and the collect poll with it, so the user lands back
 * on a dead flow having just signed successfully (KREA-4243). Letting the user
 * switch back to the browser themselves leaves the page untouched, and the poll is
 * still running when they do.
 *
 * Callers that pass `currentUrl` opt out of this for iOS — see `resolveRedirect`.
 */
const NO_REDIRECT = "null";

/**
 * The page wears WAITING while the BankID app is in the foreground and is sent
 * back to RETURN when the user is done. The two only exist to differ from each
 * other: if the BankID app redirected to the URL the page already has, Safari
 * reloads it instead of firing a hash change, which destroys the React state — and
 * with it the orderRef of the authentication the user just completed. Neither hash
 * has a DOM target or a listener, and neither carries any data.
 */
export const BANKID_WAITING_HASH = "#bankid-container";
const BANKID_RETURN_HASH = "#bankid";

interface IBuildAutostartUrlParams {
  autoStartToken: string;
  userAgent: string;
  /**
   * `window.location.href` at the moment of the hand-off. Supplying it opts this
   * hand-off into an iOS return URL; omitting it keeps {@link NO_REDIRECT}
   * everywhere. Only the `/identify` flow opts in — see `returnToBrowserOnIOS`
   * in `common-types.ts`.
   */
  currentUrl?: string;
}

export const isIOS = (userAgent: string) => /iPad|iPhone|iPod/.test(userAgent);

export const isMobileUserAgent = (userAgent: string) =>
  /iPhone|iPad|iPod|Android/i.test(userAgent);

const withReturnHash = (currentUrl: string) => {
  const url = new URL(currentUrl);
  url.hash = BANKID_RETURN_HASH;

  return url.toString();
};

/**
 * Where the BankID app should send the user once they are done.
 *
 * Chrome and Firefox get their own scheme rather than a URL: it foregrounds the
 * browser without navigating the page at all, so nothing can be reloaded away.
 * Safari and every in-app WebView we cannot identify get the page URL back with a
 * different hash — a hash-only change fires `hashchange` instead of a reload.
 * Never the literal "null" on iOS: an empty redirect strands the user inside the
 * BankID app, which is the regression this branch exists to avoid.
 *
 * Android and desktop keep {@link NO_REDIRECT} whether or not `currentUrl` was
 * given — they return to the browser on their own.
 */
const resolveRedirect = (userAgent: string, currentUrl: string | undefined) => {
  if (!currentUrl || !isIOS(userAgent)) return NO_REDIRECT;

  if (userAgent.includes("CriOS")) return "googlechrome://";

  if (userAgent.includes("FxiOS")) return "firefox://";

  return withReturnHash(currentUrl);
};

/**
 * The in-app browsers that cannot open a custom scheme at all, so the BankID app
 * has to be reached through its universal link instead.
 */
const needsUniversalLink = (userAgent: string) =>
  isIOS(userAgent) && /FBAN|FBAV|Instagram/i.test(userAgent);

export const buildBankIDAutostartUrl = ({
  autoStartToken,
  userAgent,
  currentUrl,
}: IBuildAutostartUrlParams) => {
  const base = needsUniversalLink(userAgent)
    ? BANKID_UNIVERSAL_LINK
    : BANKID_SCHEME;

  const params = new URLSearchParams({
    autostarttoken: autoStartToken,
    redirect: resolveRedirect(userAgent, currentUrl),
  });

  return `${base}?${params.toString()}`;
};
